import type { Level, MusicCue, SpawnContext, TimelineEvent } from "../content/levelTypes.ts";

/**
 * Drives a Level's deterministic timeline. Advances an internal clock in sim
 * steps and fires spawn events / music cues as their timestamps are crossed.
 * Because events are pre-sorted and fired by index, playback is identical every
 * run for a given seed — the basis for memorize-and-master play.
 */
export class TimelineRunner {
  private events: TimelineEvent[] = [];
  private cues: MusicCue[] = [];
  private evIdx = 0;
  private cueIdx = 0;
  time = 0;
  duration = 0;

  constructor(private readonly onCue: (cue: string) => void) {}

  load(level: Level): void {
    this.events = [...level.events].sort((a, b) => a.t - b.t);
    this.cues = [...level.music].sort((a, b) => a.t - b.t);
    this.evIdx = 0;
    this.cueIdx = 0;
    this.time = 0;
    this.duration = level.duration;
  }

  get progress(): number {
    return this.duration > 0 ? Math.min(1, this.time / this.duration) : 0;
  }

  get finished(): boolean {
    return this.time >= this.duration;
  }

  step(dt: number, ctx: SpawnContext): void {
    this.time += dt;
    while (this.evIdx < this.events.length && this.events[this.evIdx].t <= this.time) {
      this.events[this.evIdx].fire(ctx);
      this.evIdx++;
    }
    while (this.cueIdx < this.cues.length && this.cues[this.cueIdx].t <= this.time) {
      this.onCue(this.cues[this.cueIdx].cue);
      this.cueIdx++;
    }
  }
}
